import React from 'react';
import TableItem from '../Table';
import BarStackedChartItem from '../BarStackedChartItem';
import BarChartItem from '../BarChartItem';
import PieChartItem from '../PieChartItem';

interface OperationalTabProps {
  companyData: any;
}

export default function OperationalTab({ companyData }: OperationalTabProps) {
  if (!companyData) return null;

  return (
    <div className="space-y-8 min-w-0">
      {/* Revenues Analytics */}
      {companyData.revenues_analytics && (
        <section className="bg-white rounded-xl border border-slate-200 p-6 overflow-hidden">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Revenues Analytics</h3>

          <TableItem table={companyData.revenues_analytics} head_bold={true} bold_column_indexes={[0]} />

          {companyData.revenues_analytics_chart_data && (
            <div className="mt-6">
              <BarStackedChartItem
                name="Revenues Analytics"
                x_axis={companyData.revenues_analytics_chart_data.x_axis}
                data={companyData.revenues_analytics_chart_data.data}
              />
            </div>
          )}

          {companyData.revenues_analytics_percents && (
            <div className="mt-8">
              <h4 className="text-base font-medium text-slate-700 mb-3">Percentage Breakdown</h4>
              <TableItem table={companyData.revenues_analytics_percents} head_bold={true} bold_column_indexes={[0]} />

              {companyData.revenues_analytics_percents_chart_data && (
                <div className="mt-6">
                  <BarStackedChartItem
                    name="Revenues Analytics (%)"
                    x_axis={companyData.revenues_analytics_percents_chart_data.x_axis}
                    data={companyData.revenues_analytics_percents_chart_data.data}
                  />
                </div>
              )}
            </div>
          )}

          {companyData.revenues_analytics_totals && (
            <div className="mt-6">
              <h4 className="text-base font-medium text-slate-700 mb-3">Totals</h4>
              <TableItem table={companyData.revenues_analytics_totals} head_bold={true} bold_column_indexes={[0]} />
              {companyData.revenues_analytics_totals_percents && (
                <div className="mt-4">
                  <TableItem table={companyData.revenues_analytics_totals_percents} head_bold={true} bold_column_indexes={[0]} />
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Revenues Sizing by Debt */}
      {companyData.revenues_sizing_values_by_debt && companyData.revenues_sizing_values_by_debt.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 p-6 overflow-hidden">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Revenues Sizing by Debt</h3>

          <div className="space-y-8">
            {companyData.revenues_sizing_values_by_debt.map((revenues_sizing: any, index: number) => (
              <div key={index} className="border-t border-slate-100 pt-6 first:border-t-0 first:pt-0">
                <h4 className="text-base font-medium text-slate-700 mb-4">{revenues_sizing.debt_sized_name}</h4>

                {revenues_sizing.revenues_sizing_values_total && (
                  <TableItem table={revenues_sizing.revenues_sizing_values_total} head_bold={true} bold_column_indexes={[0]} />
                )}

                {revenues_sizing.revenues_sizing_values && (
                  <div className="mt-4">
                    <TableItem table={revenues_sizing.revenues_sizing_values} head_bold={true} bold_column_indexes={[0]} />
                  </div>
                )}

                {revenues_sizing.revenues_sizing_values_chart_data && (
                  <div className="mt-6">
                    <BarChartItem
                      name="Revenues by Sizing Categories"
                      x_axis={revenues_sizing.revenues_sizing_values_chart_data.x_axis}
                      data={revenues_sizing.revenues_sizing_values_chart_data.data}
                    />
                  </div>
                )}

                {revenues_sizing.revenues_sizing_values_percents && (
                  <div className="mt-6">
                    <TableItem table={revenues_sizing.revenues_sizing_values_percents} head_bold={true} bold_column_indexes={[0]} />
                  </div>
                )}

                {revenues_sizing.revenues_sizing_values_total_percents_chart_data && (
                  <div className="mt-6">
                    <PieChartItem
                      name="Revenues Sizing - Total (%)"
                      data={revenues_sizing.revenues_sizing_values_total_percents_chart_data.data}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* OPEX Analytics */}
      {companyData.opex_analytics && (
        <section className="bg-white rounded-xl border border-slate-200 p-6 overflow-hidden">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">OPEX Analytics</h3>

          <TableItem table={companyData.opex_analytics} head_bold={true} bold_column_indexes={[0]} />

          {companyData.opex_analytics_chart_data && (
            <div className="mt-6">
              <BarStackedChartItem
                name="OPEX Analytics"
                x_axis={companyData.opex_analytics_chart_data.x_axis}
                data={companyData.opex_analytics_chart_data.data}
              />
            </div>
          )}

          {companyData.opex_analytics_percents && (
            <div className="mt-8">
              <h4 className="text-base font-medium text-slate-700 mb-3">Percentage Breakdown</h4>
              <TableItem table={companyData.opex_analytics_percents} head_bold={true} bold_column_indexes={[0]} />

              {companyData.opex_analytics_percents_chart_data && (
                <div className="mt-6">
                  <BarStackedChartItem
                    name="OPEX Analytics (%)"
                    x_axis={companyData.opex_analytics_percents_chart_data.x_axis}
                    data={companyData.opex_analytics_percents_chart_data.data}
                  />
                </div>
              )}
            </div>
          )}

          {companyData.opex_analytics_totals && (
            <div className="mt-6">
              <h4 className="text-base font-medium text-slate-700 mb-3">Totals</h4>
              <TableItem table={companyData.opex_analytics_totals} head_bold={true} bold_column_indexes={[0]} />
              {companyData.opex_analytics_totals_percents && (
                <div className="mt-4">
                  <TableItem table={companyData.opex_analytics_totals_percents} head_bold={true} bold_column_indexes={[0]} />
                </div>
              )}
            </div>
          )}

          {companyData.opex_over_revenues && (
            <div className="mt-8">
              <h4 className="text-base font-medium text-slate-700 mb-3">OPEX over Revenues</h4>
              <TableItem table={companyData.opex_over_revenues} head_bold={true} bold_column_indexes={[0]} />
              {companyData.opex_over_revenues_totals && (
                <div className="mt-4">
                  <TableItem table={companyData.opex_over_revenues_totals} head_bold={true} bold_column_indexes={[0]} />
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
